import React, { useState } from "react";
import { TextField, Button, Typography, Box, Container, InputAdornment, IconButton, CircularProgress, Alert } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { createUserWithEmailAndPassword } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, setDoc } from "firebase/firestore";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import EmailIcon from "@mui/icons-material/Email";
import LockIcon from "@mui/icons-material/Lock";
import PersonIcon from "@mui/icons-material/Person";
import { motion } from "framer-motion";
import logo from "../assets/logo.png";

const cyan = "#00cba9";
const purple = "#a855f7";

function Signup() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  const handleSignup = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");

    // Confirm password validation
    if (password !== confirmPassword) {
      setError("Passwords do not match!");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      return;
    }
    if (!email.includes("@")) {
      setError("Please enter a valid email address.");
      return;
    }
    if (name.trim().length < 2) {
      setError("Please enter your full name.");
      return;
    }

    setLoading(true);
    try {
      // Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Store user info (default role is "student", but you control this manually in console)
      await setDoc(doc(db, "users", user.uid), {
        name,
        email,
        role: "student",
        createdAt: new Date()
      });
      setSuccess("Signup successful! Redirecting to login...");
      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (error) {
      setError(error.message);
    }
    setLoading(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: -100 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 100 }}
      transition={{ duration: 0.5 }}
    >
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          background: `linear-gradient(135deg, ${cyan}15, ${purple}15)`,
          position: "relative",
          overflow: "hidden"
        }}
      >
        {/* Animated Background Circles */}
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            rotate: [0, 180, 360]
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            position: "absolute",
            width: 400,
            height: 400,
            borderRadius: "50%",
            background: `${cyan}20`,
            top: -100,
            right: -100
          }}
        />
        <motion.div
          animate={{
            scale: [1, 1.3, 1],
            rotate: [360, 180, 0]
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{
            position: "absolute",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `${purple}20`,
            bottom: -50,
            left: -50
          }}
        />

        <Container maxWidth="sm" sx={{ position: "relative", zIndex: 1 }}>
          <Box
            sx={{
              bgcolor: "white",
              borderRadius: 4,
              p: 5,
              boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
              backdropFilter: "blur(10px)"
            }}
          >
            {/* Logo & Title */}
            <Box display="flex" flexDirection="column" alignItems="center" mb={4}>
              <motion.img
                src={logo}
                alt="Dr. Labeeb Academy"
                style={{ height: 60, marginBottom: 16 }}
                whileHover={{ scale: 1.1, rotate: 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
              <Typography
                variant="h4"
                fontWeight={900}
                sx={{
                  background: `linear-gradient(135deg, ${cyan}, ${purple})`,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  mb: 1
                }}
              >
                Create an Account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Signup to start your learning journey!
              </Typography>
            </Box>

            <Box component="form" onSubmit={handleSignup}>
              {/* Name Field */}
              <TextField
                fullWidth
                label="Full Name"
                variant="outlined"
                margin="normal"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: cyan }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,203,169,0.15)"
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 16px rgba(0,203,169,0.2)"
                    }
                  }
                }}
              />

              {/* Email Field */}
              <TextField
                fullWidth
                label="Email Address"
                variant="outlined"
                margin="normal"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailIcon sx={{ color: cyan }} />
                    </InputAdornment>
                  )
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,203,169,0.15)"
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 16px rgba(0,203,169,0.2)"
                    }
                  }
                }}
              />

              {/* Password Field */}
              <TextField
                fullWidth
                label="Password"
                type={showPassword ? "text" : "password"}
                variant="outlined"
                margin="normal"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: cyan }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,203,169,0.15)"
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 16px rgba(0,203,169,0.2)"
                    }
                  }
                }}
              />

              {/* Confirm Password Field */}
              <TextField
                fullWidth
                label="Confirm Password"
                type={showConfirmPassword ? "text" : "password"}
                variant="outlined"
                margin="normal"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockIcon sx={{ color: cyan }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)} edge="end">
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: 2,
                    transition: "all 0.3s ease",
                    "&:hover": {
                      boxShadow: "0 4px 12px rgba(0,203,169,0.15)"
                    },
                    "&.Mui-focused": {
                      boxShadow: "0 4px 16px rgba(0,203,169,0.2)"
                    }
                  }
                }}
              />

              {/* Error Message */}
              {error && (
                <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                  {error}
                </Alert>
              )}

              {/* Success Message */}
              {success && (
                <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                  {success}
                </Alert>
              )}

              {/* Signup Button */}
              <Button
                fullWidth
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  mt: 3,
                  py: 1.5,
                  fontSize: 16,
                  fontWeight: 700,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${cyan}, ${purple})`,
                  textTransform: "none"
                }}
              >
                {loading ? <CircularProgress size={24} sx={{ color: "white" }} /> : "Sign Up"}
              </Button>

              {/* Login Link */}
              <Box display="flex" justifyContent="center" mt={3}>
                <Typography variant="body2" color="text.secondary">
                  Already have an account?{" "}
                  <Typography
                    component="span"
                    sx={{
                      color: cyan,
                      fontWeight: 700,
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        textDecoration: "underline",
                        color: purple
                      }
                    }}
                    onClick={() => navigate("/login")}
                  >
                    Login
                  </Typography>
                </Typography>
              </Box>

              {/* Back to Home */}
              <Box display="flex" justifyContent="center" mt={2}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748b",
                    cursor: "pointer",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      color: cyan
                    }
                  }}
                  onClick={() => navigate("/")}
                >
                  ← Back to Home
                </Typography>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>
    </motion.div>
  );
}

export default Signup;
